import * as React from 'react';

import { Typography } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import InputBase from '@material-ui/core/InputBase';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { compose } from 'recompose';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'stretch',
      flexDirection: 'row',
      justifyContent: 'center',
      position: 'relative',
      width: '100%',
    },
    display: {
      position: 'absolute',
      margin: '0 -4px',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-around',
      alignItems: 'center',
    },
    cell: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    cellDefault: {
      borderBottomColor: theme.palette.text.hint,
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
    },
    '@keyframes blink-animation': {
      to: {
        visibility: 'hidden',
      },
    },
    cellFocusedDefault: {
      borderBottomColor: theme.palette.primary.main,
      animation: `$blink-animation 1s steps(7, start) infinite`,
    },
    textStyleDefault: {
      color: 'rgba(0, 0, 0, 0.87)',
      fontSize: '16px',
      lineHeight: 1.5,
    },
    textStyleFocusedDefault: {
      // color: 'black',
    },
    error: {
      color: theme.palette.error.main,
    },
    inputEl: {
      flex: 1,
      opacity: 0,
      // bigger font size so mobile wouldn't zoom in
      fontSize: 24,
      textAlign: 'center',
      color: 'transparent',
      textShadow: '0 0 0 #000',
      caretColor: 'transparent',
      // OMG don't use userSelect, brakes Safari
      // userSelect: 'none',
      '&:focus': {
        outline: 'none',
      },
    },
  });

const Cell = ({
  className,
  textClassName,
  cellFocused,
  cellSize,
  cellSpacing,
  filled,
  error,
  children,
}) => (
  <div
    className={className}
    style={{
      width: cellSize,
      height: cellSize,
      marginLeft: cellSpacing / 2,
      marginRight: cellSpacing / 2,
    }}
  >
    <Collapse in={filled}>
      <Typography className={textClassName}>{children}</Typography>
    </Collapse>
  </div>
);

export interface IPinCodeInputProps {
  id?: string;
  value?: string;
  codeLength?: number;
  cellSize?: number;
  cellSpacing?: number;
  placeholder?: string;
  password?: boolean;
  mask?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
  onBackspace?();
  onTextChange?(change: string);
  onFulfill?(pin: string);
}

type Props = WithStyles<typeof styles> & IPinCodeInputProps;
const PinCodeInput = compose<Props, IPinCodeInputProps>(
  withStyles(styles, {
    name: 'FdPinCodeInput',
    withTheme: true,
  })
)(
  class PinCodeInput extends React.Component<Props, any> {
    public static defaultProps = {
      value: '',
      codeLength: 6,
      cellSize: 36,
      cellSpacing: 8,
      placeholder: '',
      password: false,
      mask: '*',
      autoFocus: false,
      animationFocused: 'pulse',
    };

    public state = {
      code: '',
      maskDelay: false,
      focused: false,
    };

    private ref = React.createRef<HTMLDivElement>();
    private inputRef = React.createRef<HTMLInputElement>();

    public componentWillMount() {
      this.setState({ code: this.props.value || this.state.code });
    }
    public componentDidMount() {
      if (!this.inputRef.current) {
        return;
      }
      if (this.props.autoFocus) {
        // helpers for mobile (autofocus on input doesn't work every time as expected)
        this.inputRef.current.scrollIntoView();
        this.inputRef.current.focus();
      }
    }
    public componentDidUpdate(prevProps: Props) {
      if (!prevProps.error && this.props.error === true) {
        this.setState({ code: '' });
      }
    }

    public shake = () => {
      // TODO: shaking animation on error for mobile
      if (this.inputRef.current) {
        // return this.ref.current.shake(650);
      }
    };

    public focus = () => {
      if (this.inputRef.current) {
        this.inputRef.current.focus();
      }
    };

    public blur = () => {
      if (this.inputRef.current) {
        this.inputRef.current.blur();
      }
    };

    public render() {
      const {
        id,
        codeLength,
        cellSize,
        cellSpacing,
        placeholder,
        password,
        mask,
        classes,
        disabled,
        error,
      } = this.props;
      const { maskDelay, focused, code: value } = this.state;

      return (
        <div
          ref={this.ref}
          className={classes.root}
          style={{
            height: cellSize,
          }}
        >
          <div className={classes.display}>
            {[...Array(codeLength)].map((_, idx) => {
              const cellFocused = focused && idx === value!.length;
              const filled = idx < value!.length;
              const last = idx === value!.length - 1;

              const spacer = idx === codeLength! / 2 - 1 && (
                <Cell
                  key={'spacer'}
                  cellSize={cellSize! - 12}
                  cellFocused={false}
                  cellSpacing={cellSpacing}
                  error={error}
                  filled={filled}
                  className={classnames(classes.cell)}
                  textClassName={classnames(classes.textStyleDefault, {
                    [classes.error]: error,
                  })}
                >
                  {filled ? '—' : ''}
                </Cell>
              );

              return (
                <>
                  <Cell
                    key={idx}
                    cellSize={cellSize}
                    cellFocused={cellFocused}
                    cellSpacing={cellSpacing}
                    error={error}
                    filled={filled}
                    className={classnames(classes.cell, classes.cellDefault, {
                      [classes.cellFocusedDefault]: cellFocused,
                    })}
                    textClassName={classnames(classes.textStyleDefault, {
                      [classes.textStyleFocusedDefault]: cellFocused,
                      [classes.error]: error,
                    })}
                  >
                    {filled && password && (!maskDelay || !last) ? mask : value!.charAt(idx)}
                    {!filled && placeholder}
                  </Cell>
                  {spacer}
                </>
              );
            })}
          </div>
          <InputBase
            inputRef={this.inputRef}
            id={id}
            value={value}
            onChange={this.handleInput}
            onKeyPress={this.handleKeyPress}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onPaste={this.handlePaste}
            disabled={disabled}
            fullWidth
            inputProps={{
              className: classes.inputEl,
              pattern: '[0-9]*',
              maxLength: codeLength,
              spellCheck: false,
              autoComplete: 'off',
              autoCorrect: 'off',
              inputMode: 'numeric',
              'data-fd': 'pin_input',
              'data-lpignore': 'true',
            }}
          />
        </div>
      );
    }

    private handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { password, codeLength, onTextChange, onFulfill } = this.props;

      const codeMatch = event.target.value === '' ? [''] : event.target.value.match(/\d+/g);
      if (!codeMatch) {
        return;
      }
      const code = codeMatch.join('');
      if (code.length > codeLength!) {
        return;
      }

      // handle password mask
      const maskDelay = password && code.length > this.state.code.length; // only when input new char
      this.setState({ code, maskDelay });

      if (maskDelay) {
        // mask password after delay
        setTimeout(() => this.setState({ maskDelay: false }), 400);
      }

      if (onTextChange) {
        onTextChange(code);
      }
      if (code.length === codeLength && onFulfill) {
        onFulfill(code);
      }
    };

    private handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Backspace') {
        const { value, onBackspace } = this.props;
        if (value === '' && onBackspace) {
          onBackspace();
        }
      }
    };

    private handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
      const selectionEnd = this.props.value!.length;
      if (this.inputRef.current) {
        this.inputRef.current.setSelectionRange(selectionEnd, selectionEnd);
      }
      this.setState({ focused: true });
    };
    private handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      this.setState({ focused: false });
    };

    private handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
      const { codeLength, onTextChange, onFulfill } = this.props;
      const codeMatch = event.clipboardData.getData('text/plain').match(/\d+/g);
      if (!codeMatch) {
        return;
      }

      const code = codeMatch.join('');
      if (code.length > codeLength!) {
        return;
      }

      this.setState({ code });

      if (onTextChange) {
        onTextChange(code);
      }
      if (code.length === codeLength && onFulfill) {
        onFulfill(code);
      }
    };
  }
);

export default PinCodeInput;
